import Button from "@src/components/common/Button";
import Image from "next/image";
import React, { ReactNode } from "react";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  description: string | ReactNode;
  confirmText: string;
  cancelText: string;
  children?: ReactNode;
  isLoading?: boolean;
  type: "danger" | "warning" | "info";
  width?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  children,
  isLoading,
  type,
  width,
}: ConfirmModalProps): JSX.Element => {
  if (!isOpen) return null;

  return (
    <div
      className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none bg-zinc-800 bg-opacity-70 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
      onKeyDown={e => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div
        className={`relative my-6 mx-auto ${width ? width : "w-auto"}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="border-0 rounded-xl shadow-modal relative flex flex-col w-full bg-neutral-0 outline-none focus:outline-none">
          <div className="flex justify-between items-start p-6 pt-8 border-b border-solid border-neutral-200 rounded-t">
            <div className="flex items-start">
              {type !== "info" && (
                <div
                  className={`${
                    type === "danger" ? "bg-red-50" : "bg-yellow-50"
                  } p-4 mr-4 rounded-full flex justify-center items-center shrink-0`}
                >
                  <Image
                    src={`${type === "danger" ? "/warning2.svg" : "/warningTriangle.svg"}`}
                    alt="Warning"
                    width={24}
                    height={24}
                  />
                </div>
              )}
              <div>
                <h2 className="text-[17px] font-semibold text-neutral-900">{title}</h2>
                <p className="text-[15px] text-neutral-400 mt-1">{description}</p>
              </div>
            </div>
            <button className="text-slate-400 hover:text-neutral-800 shrink-0" onClick={onClose}>
              <Image src="/x_close.svg" alt="Close" width={24} height={24} />
            </button>
          </div>
          <div className="p-6">
            {children}
            <div className="flex justify-end gap-4">
              <Button variant="secondary" size="small" className="max-w-full" onClick={onCancel}>
                {cancelText}
              </Button>
              <Button
                variant={type === "danger" ? "danger" : "afya"}
                size="small"
                className="max-w-full"
                onClick={onConfirm}
                isLoading={isLoading}
                disabled={isLoading}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
